import React, { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from 'app/state/contexts/UserContext';
import request from 'app/api/request';
import { Spinner } from 'app/components';
import Prism from 'prismjs';
import { v4 as uuidv4 } from 'uuid';
import 'prismjs/themes/prism.css';
import ServerErrorHandler from 'app/ErrorHandler';
import { useStyles } from 'app/api/style-queries';
import { useGetDocument } from 'app/api/documents/document-queries';
import { i18n } from 'app/utils/i18n';
import { useDocumentTemplates } from 'app/api/template-queries';
import { useGetSectionPages } from 'app/api/documents/page-queries';
const actions = {
  DOCUMENT_ACTION: 'update_document_style',
  TEMPLATE_ACTION: 'update_template_style',
};

const getActionForStyle = (document, styleId) => {
  let type = 'document';

  if (document?.content?.design) {
    const style = document.content.design.find((s) => s.styleId === styleId);

    if (style) {
      type = style.type;
    }
  }
  switch (type) {
    case 'template':
      return actions.TEMPLATE_ACTION;
    default:
      return actions.DOCUMENT_ACTION;
  }
};

export const CodeBlock = ({ style = {}, onSave = null, onDelete }) => {
  const codeRef = useRef(null);

  const [editMode, setEditMode] = useState(false);
  const [height, setHeight] = useState(0);
  const [content, setContent] = useState(style.content);

  useEffect(() => {
    setContent(style.content);
    setEditMode(false);
  }, [style.content]);

  useEffect(() => {
    if (!editMode) {
      Prism.highlightAll();
    }
  }, [editMode]);

  const handleClickEdit = () => {
    // Get the height of the box so we can match the textareas height.
    setHeight(codeRef.current.getBoundingClientRect().height);
    setEditMode(true);
  };

  const handleSave = () => {
    onSave(style, content);
  };

  const handleOnChangeContent = (e) => {
    setContent(e.target.value);
  };

  return (
    <div className="card mb-3">
      <div className="card-body position-relative">
        {editMode ? (
          <>
            <textarea style={{ height: `${height}px` }} className="bg-light w-100 border-0 resize-none" value={content} onChange={handleOnChangeContent} />
            <button className="btn btn-secondary btn-sm position-absolute top-1 end-1" onClick={handleSave}>
              <i className="fa-regular fa-floppy-disk" />
            </button>
          </>
        ) : (
          <>
            <pre className="bg-light">
              <code className="language-css" ref={codeRef}>
                {style.content}
              </code>
            </pre>
            <button className="btn btn-secondary btn-sm position-absolute top-1 end-1" onClick={handleClickEdit}>
              <i className="fa-regular fa-pen" />
            </button>
            {onDelete && (
              <button className="btn btn-secondary btn-sm position-absolute top-1 end-4" onClick={onDelete}>
                <i className="fa-regular fa-trash" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ProjectCSS = ({ documentId = null }) => {
  const { selectedOrganization } = useContext(UserContext);
  const { data: pDocument, isLoading: isDocumentLoading, refetch: refetchDocument } = useGetDocument(documentId, selectedOrganization);
  const styleIds = pDocument.content.design.map((d) => d.styleId);
  const { data: styles, isLoading: isStylesLoading } = useStyles({
    organization: selectedOrganization,
    documentRevisionId: pDocument.revisionId,
    styleIds,
    parsed: false,
  });
  const { data: documentTemplates, isLoading: isDocumentTemplatesLoading } = useDocumentTemplates(documentId);
  const sectionRevisionIds = (pDocument.content.sections ?? []).map((s) => s.revisionId);
  const { data: sectionPages, isLoading: isSectionPagesLoading } = useGetSectionPages(pDocument.revisionId, sectionRevisionIds);
  const pageInfoById = (sectionPages ?? []).flat().reduce((acc, p) => {
    if (p.pageRevisionId) acc[p.pageRevisionId] = p;
    return acc;
  }, {});
  useEffect(() => {
    if (styles) {
      Prism.highlightAll();
    }
  }, [styles]);

  const handleSaveStyle = async (oldStyle = {}, newContent = '') => {
    const newStyle = {
      ...oldStyle,
      id: uuidv4(),
      content: newContent,
    };

    const type = pDocument.content.design.find((d) => d.styleId === oldStyle.id)?.type || 'document';
    request
      .post(`/gaby/styles`, newStyle)
      .then((res) => {
        return request
          .post(`/gaby/documents/${documentId}/actions?compileSass=true`, {
            action: getActionForStyle(pDocument, oldStyle.id),
            revisionId: oldStyle.id,
            data: {
              styleId: newStyle.id,
              type,
            },
          })
          .then(() => {
            refetchDocument();
            return res;
          });
      })
      .catch(ServerErrorHandler);
  };
  if (isStylesLoading || isDocumentLoading || isDocumentTemplatesLoading || isSectionPagesLoading) {
    return <Spinner />;
  }
  const typeOrder = ['graphical-variables', 'paper', 'document', 'form-classes', 'template', 'page-variables'];
  const sortedStyles = [...styles].sort((a, b) => {
    const designA = pDocument.content.design.find((d) => d.styleId === a.id);
    const designB = pDocument.content.design.find((d) => d.styleId === b.id);
    const orderA = designA ? typeOrder.indexOf(designA.type) : typeOrder.length;
    const orderB = designB ? typeOrder.indexOf(designB.type) : typeOrder.length;
    return (orderA === -1 ? typeOrder.length : orderA) - (orderB === -1 ? typeOrder.length : orderB);
  });

  return (
    <>
      {sortedStyles.map((style, index) => {
        const design = pDocument.content.design.find((d) => d.styleId === style.id);
        if (!design) return null;
        return (
          <React.Fragment key={style.id}>
            <div className="fw-bold mt-4 mb-2">{getDetails(design, documentTemplates, pageInfoById)}</div>
            <CodeBlock style={style} onSave={handleSaveStyle} />
          </React.Fragment>
        );
      })}
    </>
  );
};

const getDetails = (design, templates, pageInfoById = {}) => {
  switch (design.type) {
    case 'document':
      return `${design.type} (${design.templateSuperId}) `;
    case 'template':
      const template = templates.find((t) => t.superId === design.templateSuperId);
      return `${design.type} - ${template ? `${template.name}` : i18n('project-details.project-css.no-template')} (.xrp-template-${design.templateSuperId})`;
    case 'graphical-variables':
      return design.type;
    case 'page-variables': {
      const info = pageInfoById[design.pageId];
      if (info) {
        return `${design.type} \n ${info.name} - p.${info.internalIdx + 1} (#id-${design.pageId})`;
      }
      return `${design.type} (#id-${design.pageId})`;
    }
    case 'form-classes':
      return 'form-classes';
    default:
      return design.type;
  }
};

export default ProjectCSS;
